import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WSContext = createContext(null);

const WS_URL = API_BASE.replace(/^http/, "ws") + "/ws";

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const listeners = useRef(new Set());
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const closedByUs = useRef(false);
  const [online, setOnline] = useState(new Set());
  const [connected, setConnected] = useState(false);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const emit = (evt) => listeners.current.forEach((fn) => fn(evt));

  const connect = useCallback(() => {
    if (!user) return;
    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" }));
        }, 25000);
      };

      ws.onmessage = (e) => {
        let data;
        try {
          data = JSON.parse(e.data);
        } catch {
          return;
        }
        if (data.type === "presence_init") {
          setOnline(new Set(data.online));
          return;
        }
        if (data.type === "presence") {
          setOnline((prev) => {
            const n = new Set(prev);
            if (data.online) n.add(data.userId);
            else n.delete(data.userId);
            return n;
          });
          return;
        }
        if (data.type === "pong") return;
        emit(data);
      };

      ws.onclose = async () => {
        setConnected(false);
        clearInterval(pingRef.current);
        if (closedByUs.current) return;
        // try to refresh access token then reconnect
        try {
          await api.post("/auth/refresh");
        } catch (e) {
          /* ignore */
        }
        clearTimeout(reconnectRef.current);
        reconnectRef.current = setTimeout(connect, 2000);
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {}
      };
    } catch (e) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = setTimeout(connect, 3000);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    closedByUs.current = false;
    connect();
    return () => {
      closedByUs.current = true;
      clearTimeout(reconnectRef.current);
      clearInterval(pingRef.current);
      try {
        wsRef.current?.close();
      } catch {}
      setConnected(false);
      setOnline(new Set());
    };
  }, [user, connect]);

  const sendTyping = useCallback((conversationId, isTyping) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: "typing", conversationId, isTyping }));
    }
  }, []);

  return (
    <WSContext.Provider value={{ subscribe, online, connected, sendTyping }}>
      {children}
    </WSContext.Provider>
  );
}

export const useWS = () => useContext(WSContext);
